import { themesMap } from '@Assets/Themes'
import { DEFAULT_THEME_KEY, isKnownThemeKey, LEGACY_THEME_KEYS, ThemeKey } from '@Assets/Themes/themes.map'
import { ITheme } from '@eduact/student-theme'
import { useAppSelector } from '@Hooks/ReduxHooks'
import React, { Dispatch, useEffect, useState } from 'react'

type ThemeSelectorProps = {
	children: (theme: ITheme, setTheme: Dispatch<ITheme>) => JSX.Element
}

const THEME_STORAGE_KEY = 'theme-key'

/**
 * Resolve the active theme key.
 *
 * AbwaabPlus is the product default. Resolution order:
 *   1. An explicit stored choice (`theme-key`) — wins even if it is a legacy
 *      theme, so the legacy look stays selectable via the existing mechanism.
 *   2. A backend-pushed theme — honored only when it is a known, NON-legacy key
 *      (lets the backend roll out future themes without resurrecting the old one).
 *   3. AbwaabPlus default — legacy/unknown backend values (`primary`/`secondary`)
 *      coerce here so the redesign is what every user sees by default.
 */
const resolveThemeKey = (stored: string | null, backend: string | null | undefined): ThemeKey => {
	if (isKnownThemeKey(stored)) return stored
	if (isKnownThemeKey(backend) && !LEGACY_THEME_KEYS.includes(backend)) return backend
	return DEFAULT_THEME_KEY
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ children }) => {
	const [theme, setTheme] = useState<ITheme | undefined>()
	const [isLoading, setIsLoading] = useState(true)
	const existedTheme = useAppSelector(s => s.settings.theme)

	useEffect(() => {
		setIsLoading(true)
		const stored = localStorage.getItem(THEME_STORAGE_KEY)
		const key = resolveThemeKey(stored, existedTheme)
		setTheme(themesMap[key])
		setIsLoading(false)
	}, [existedTheme])

	return <>{!isLoading && theme && <>{children(theme, setTheme)}</>}</>
}
